/**
 * @module LocationsHelper
 */
const { toType } = require('./toType');
const { US_STATES } = require('../constants');

/**
 * @typedef StateProps
 * @property {string} code - Two-letter state code.
 * @property {string} name - The full state name.
 */

/**
 * @typedef USStateData
 * @property {string} code - Two-letter state code.
 * @property {string} lat - The state's latitude coordinate value.
 * @property {string} lng - The state's longitude coordinate value.
 * @property {string} name - The full state name.
 */

/**
 * Returns a U.S. State-specific data object for the supplied state.
 *
 * Note: The supplied object values are converted to lowercase before matching.
 * As such, capitalization of object attributes does not matter. Additionally,
 * if both a `code` and `name` are found in the `stateProps` object parameter,
 * the `code` will be used first. If no matching state is found, the `name`
 * will be used.
 *
 * @param {object} stateProps - An `object` with either a `code` or `name` key, representing the desired state to retrieve (e.g. { code: 'OK' }, or { name: 'Oklahoma' }).
 *
 * @returns {USStateData} - The U.S. State data object.
 */
function getStateData(stateProps) {
  const { code, name } = stateProps;
  if (!code && !name) {
    return null;
  }

  let stateData;
  if (code && toType(code) === 'string') {
    stateData = US_STATES.find((item) => {
      return item.code.toLowerCase() === code.toLowerCase();
    });
  } else if (name && toType(name) === 'string') {
    stateData = US_STATES.find((item) => {
      return item.name.toLowerCase() === name.toLowerCase();
    });
  }

  // If no stateData yet, check with `name` attribute, if supplied.
  if (!stateData && name && toType(name) === 'string') {
    stateData = US_STATES.find((item) => {
      return item.name.toLowerCase() === name.toLowerCase();
    });
  }
  return stateData || null;
}

/**
 * Returns a two-letter state code from the supplied U.S. State name.
 *
 * Note: The supplied value is converted to lowercase before matching, and as such, capitalization does not matter.
 *
 * @param {object|string} value - If a string is supplied, the full state name. Alternatively, an `object` with a `name` key, representing the full state name.
 *
 * @returns {string} - The two-letter state code (e.g. OK).
 */
function getStateCodeFromName(value) {
  let stateName = '';
  if (toType(value) === 'string') {
    stateName = value;
  } else if (toType(value) === 'object') {
    stateName = value.name;
  }

  const stateDetails = US_STATES.find((item) => {
    return item.name.toLowerCase() === stateName.toLowerCase();
  });
  return (stateDetails && stateDetails.code) || null;
}

/**
 * Returns a full U.S. State name from the supplied state code.
 *
 * @param {object|string} value - If a string is supplied, the two-letter state code. Alternatively, an `object` with a `code` key, representing the two-letter state code.
 *
 * @returns {string} - The full U.S. State name (e.g. Oklahoma).
 */
function getStateNameFromCode(value) {
  let stateCode = '';
  if (toType(value) === 'string') {
    stateCode = value;
  } else if (toType(value) === 'object') {
    stateCode = value.code;
  }

  const stateDetails = US_STATES.find((item) => {
    return item.code === stateCode.toUpperCase();
  });
  return (stateDetails && stateDetails.name) || null;
}

/**
 * Updates Mapbox Map with new coordinates. Used to auto-load the map via param or for listeners to dynamically update the map.
 *
 * @param  {object} coordinates -  An object containing lat/long coordinates.
 * @param  {number} zoom - The zoom level value.
 * @param  {object} [ctx] - An object containing context from the parent caller.
 */
const setMapCoordinates = (coordinates, zoom = 8, ctx = {}) => {
  if (
    coordinates &&
    coordinates.lat !== undefined &&
    coordinates.lat !== null &&
    coordinates.lng !== undefined &&
    coordinates.lng !== null &&
    !Number.isNaN(coordinates.lat) &&
    !Number.isNaN(coordinates.lng) &&
    !Number.isNaN(zoom) &&
    toType(zoom) === 'number' &&
    ctx &&
    ctx.setCenter &&
    ctx.setZoom &&
    toType(ctx.setCenter) === 'function' &&
    toType(ctx.setZoom) === 'function'
  ) {
    const newCenter = { lat: coordinates.lat, lng: coordinates.lng };
    ctx.setCenter(newCenter);
    ctx.setZoom(zoom);
    window.scrollTo({ behavior: 'smooth', top: 0 });
  }
};

module.exports = {
  getStateCodeFromName,
  getStateData,
  getStateNameFromCode,
  setMapCoordinates,
};
